/*
 * Persistent background audio via AJAX ("pjax") navigation.
 *
 * Problem: a normal Shopify storefront reloads the whole page on every click,
 * which destroys the <audio> element, and browsers refuse to auto-resume audio
 * after a reload (a fresh user gesture is required — impossible to satisfy on
 * mobile). Result: music only plays on the page the visitor first interacted on.
 *
 * Fix: intercept internal link clicks, fetch the next page in the background,
 * and swap the DOM in place WITHOUT a full reload — keeping the background-audio
 * section (#shopify-section-background-audio) alive the whole time, so the track
 * never stops as the visitor moves around the site.
 *
 * Safety model:
 *   - Off unless enabled (window.__BK_PJAX__ set by the layout from a setting).
 *   - Disabled inside the Shopify theme editor and inside iframes.
 *   - Only intercepts plain same-origin navigational links; cart/checkout/account,
 *     downloads, new-tab, external, hash and app links are left to the browser.
 *     Any element (or ancestor) with [data-no-pjax] opts out.
 *   - If ANYTHING throws mid-swap, we fall back to a real navigation, so a bad
 *     swap self-heals into a normal page load instead of leaving a broken page.
 *
 * NOTE: this store runs GemPages and PageFly. Test every page type on an
 * unpublished/duplicate theme before publishing. Flip the setting off to disable
 * instantly with no code change.
 */
(function () {
  'use strict';

  if (window.__BK_PJAX_INIT__) return; // this script re-appears in every swapped page — only wire up once
  if (window.top !== window.self) return; // never inside an iframe/editor frame
  if (window.Shopify && window.Shopify.designMode) return; // never in the theme editor
  if (!window.__BK_PJAX__) return; // gated by the theme setting
  if (!window.history || !window.history.pushState || !window.DOMParser || !window.fetch) return;
  window.__BK_PJAX_INIT__ = true;

  // The one node we keep alive across every navigation.
  var PERSIST_SELECTOR = '#shopify-section-background-audio';

  var loading = false;

  function isPersistent(el) {
    return el && el.id === 'shopify-section-background-audio';
  }

  // Decide whether a click should be handled by AJAX instead of a real load.
  function eligibleLink(a) {
    if (!a || !a.href) return false;
    if (a.origin !== window.location.origin) return false; // external
    if (a.hasAttribute('download')) return false;
    if (a.getAttribute('rel') === 'external') return false;
    var target = a.getAttribute('target');
    if (target && target !== '_self') return false; // new tab / frame
    if (a.closest('[data-no-pjax]')) return false; // explicit opt-out

    var href = a.getAttribute('href') || '';
    if (!href || href.charAt(0) === '#') return false;
    if (/^(mailto:|tel:|sms:|javascript:)/i.test(href)) return false;

    var path = a.pathname || '';
    // Let Shopify own these flows natively (session, payment, apps, home/splash).
    if (path === '/' || path === '') return false; // home = splash, intentional full load
    if (/^\/(cart|checkout|checkouts|account|admin|apps|services|password|\d+\/(checkouts|orders))(\/|$)/.test(path)) return false;

    // Same URL as now (ignoring hash) — let the browser handle in-page anchors.
    if (a.pathname === window.location.pathname && a.search === window.location.search) return false;

    return true;
  }

  // Re-run inline scripts and load any script/stylesheet the new page introduces.
  // Scripts already present by src are skipped: their modules and custom-element
  // definitions are global and persist, and the swapped-in markup auto-upgrades.
  function loadedSrcs() {
    var set = {};
    var nodes = document.querySelectorAll('script[src]');
    for (var i = 0; i < nodes.length; i++) set[nodes[i].src] = true;
    return set;
  }

  function syncHeadAssets(newDoc) {
    var haveScript = loadedSrcs();
    var haveHref = {};
    var links = document.head.querySelectorAll('link[rel="stylesheet"][href]');
    for (var i = 0; i < links.length; i++) haveHref[links[i].href] = true;

    var newLinks = newDoc.head.querySelectorAll('link[rel="stylesheet"][href]');
    for (var j = 0; j < newLinks.length; j++) {
      if (!haveHref[newLinks[j].href]) {
        var link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = newLinks[j].getAttribute('href');
        document.head.appendChild(link);
      }
    }

    var newScripts = newDoc.head.querySelectorAll('script[src]');
    for (var k = 0; k < newScripts.length; k++) {
      if (!haveScript[newScripts[k].src]) {
        document.head.appendChild(reviveScript(newScripts[k]));
      }
    }
  }

  function reviveScript(node) {
    var s = document.createElement('script');
    for (var i = 0; i < node.attributes.length; i++) {
      s.setAttribute(node.attributes[i].name, node.attributes[i].value);
    }
    s.textContent = node.textContent;
    return s;
  }

  // Execute the scripts contained in a freshly-inserted subtree. Skip external
  // scripts already loaded elsewhere; run inline scripts and genuinely-new srcs.
  function runScripts(container) {
    var have = loadedSrcs();
    var scripts = container.querySelectorAll('script');
    for (var i = 0; i < scripts.length; i++) {
      var old = scripts[i];
      var src = old.getAttribute('src');
      if (src && have[old.src]) {
        old.parentNode.removeChild(old); // already loaded — drop the inert copy
        continue;
      }
      var fresh = reviveScript(old);
      old.parentNode.replaceChild(fresh, old);
    }
  }

  function swap(html, url, push) {
    var newDoc = new DOMParser().parseFromString(html, 'text/html');
    if (!newDoc || !newDoc.body) throw new Error('unparseable response');

    document.title = newDoc.title || document.title;

    // Drop the new page's copy of the audio section — we keep the live one.
    var dupAudio = newDoc.body.querySelector(PERSIST_SELECTOR);
    if (dupAudio) dupAudio.parentNode.removeChild(dupAudio);

    syncHeadAssets(newDoc);

    // Remove old body content except the persistent audio node.
    var oldKids = Array.prototype.slice.call(document.body.children);
    for (var i = 0; i < oldKids.length; i++) {
      if (!isPersistent(oldKids[i])) document.body.removeChild(oldKids[i]);
    }

    // Move in the new content (custom elements auto-upgrade on insert).
    var frag = document.createDocumentFragment();
    var newKids = Array.prototype.slice.call(newDoc.body.children);
    for (var j = 0; j < newKids.length; j++) frag.appendChild(newKids[j]);
    document.body.appendChild(frag);

    // Carry over body classes (theme uses them for layout state).
    document.body.className = newDoc.body.className;

    runScripts(document.body);

    if (push) window.history.pushState({ bkPjax: true }, '', url);
    window.scrollTo(0, 0);

    // Let the theme's web components and the page builders re-initialise.
    document.dispatchEvent(new Event('DOMContentLoaded', { bubbles: true, cancelable: false }));
    window.dispatchEvent(new Event('load'));
    document.dispatchEvent(new CustomEvent('bk:pjax:loaded', { detail: { url: url } }));
    if (window.Shopify && typeof window.Shopify === 'object') {
      // Nudge apps that listen for section reloads.
      document.dispatchEvent(new CustomEvent('shopify:section:load'));
    }
  }

  function navigate(url, push) {
    if (loading) return;
    loading = true;
    fetch(url, { credentials: 'same-origin', headers: { 'X-Requested-With': 'fetch' } })
      .then(function (res) {
        var type = res.headers.get('content-type') || '';
        if (!res.ok || type.indexOf('text/html') === -1) throw new Error('non-html');
        return res.text();
      })
      .then(function (html) {
        try {
          swap(html, url, push);
        } catch (e) {
          window.location.href = url; // self-heal: fall back to a real load
        }
      })
      .catch(function () {
        window.location.href = url;
      })
      .then(function () {
        loading = false;
      });
  }

  document.addEventListener(
    'click',
    function (e) {
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; // let browser open new tab etc.
      var a = e.target.closest && e.target.closest('a');
      if (!eligibleLink(a)) return;
      e.preventDefault();
      navigate(a.href, true);
    },
    false
  );

  window.addEventListener('popstate', function (e) {
    // Only handle history entries we created; ignore hash-only changes.
    if (!e.state || !e.state.bkPjax) {
      // Back to a non-pjax entry — do a real load to be safe.
      window.location.reload();
      return;
    }
    navigate(window.location.href, false);
  });

  // Tag our initial entry so back/forward can tell it apart.
  try {
    window.history.replaceState({ bkPjax: true }, '', window.location.href);
  } catch (err) {}
})();
